import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, homePath } from "@/lib/auth.jsx";
import { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import { ArrowRight, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login, resendVerification } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [err, setErr] = useState("");
  const [unverifiedEmail, setUnverifiedEmail] = useState("");
  const [resent, setResent] = useState(false);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setErr("");
    setUnverifiedEmail("");
    setResent(false);
    setLoading(true);
    try {
      const u = await login(email, password);
      const dest = homePath(u);
      navigate(location.state?.from || dest, { replace: true });
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === "object" && detail?.code === "email_unverified") {
        setUnverifiedEmail(detail.email || email);
      } else {
        setErr(formatApiError(detail) || e.message);
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    if (!unverifiedEmail) return;
    try { await resendVerification(unverifiedEmail); setResent(true); } catch { /* ignore */ }
  }

  return (
    <div className="min-h-screen flex">
      {/* Left form */}
      <div className="flex-1 flex flex-col px-6 sm:px-12 lg:px-20 py-10 bg-white">
        <Logo />
        <div className="flex-1 flex flex-col justify-center max-w-md w-full mx-auto py-12">
          <p className="text-xs uppercase tracking-[0.2em] text-propera font-semibold mb-3">Welcome Back</p>
          <h1 className="text-4xl font-bold text-neutral-900 leading-tight">
            Build wealth <br />
            <span className="serif italic text-propera-gold">back home.</span>
          </h1>
          <p className="text-neutral-500 mt-3">Sign in to access your property investments.</p>

          <form onSubmit={onSubmit} className="mt-10 space-y-4">
            <div>
              <label className="text-xs font-semibold text-neutral-700">Email</label>
              <input
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-1.5 w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-propera focus:ring-2 focus:ring-propera/15 outline-none transition"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-700">Password</label>
              <div className="relative mt-1.5">
                <input
                  data-testid="login-password"
                  type={showPwd ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-propera focus:ring-2 focus:ring-propera/15 outline-none transition pr-12"
                />
                <button type="button" onClick={() => setShowPwd((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700">
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {err && (
              <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">{err}</div>
            )}

            {unverifiedEmail && (
              <div data-testid="login-unverified" className="text-sm bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                <p className="font-semibold text-amber-900">Email not verified</p>
                <p className="text-amber-800 mt-0.5">We sent a verification link to <b>{unverifiedEmail}</b>. Tap it to activate your account.</p>
                <button type="button" data-testid="login-resend-verification" onClick={handleResend} disabled={resent} className="mt-2 text-amber-900 font-semibold underline disabled:opacity-60">
                  {resent ? "Sent ✓" : "Resend verification email"}
                </button>
              </div>
            )}

            <div className="flex justify-end -mt-1">
              <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-semibold text-propera hover:underline">
                Forgot password?
              </Link>
            </div>

            <button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full bg-propera hover:bg-propera-hover text-white rounded-full px-6 py-3.5 font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-60 mt-2 glow-primary"
            >
              {loading ? "Signing in..." : (<>Sign In <ArrowRight size={16} /></>)}
            </button>

            <div className="flex items-center gap-3 my-1">
              <div className="flex-1 h-px bg-neutral-200" />
              <span className="text-[11px] uppercase tracking-widest text-neutral-400">or</span>
              <div className="flex-1 h-px bg-neutral-200" />
            </div>

            <GoogleSignInButton label="Continue with Google" testId="login-google" />
          </form>

          <p className="text-sm text-neutral-500 mt-8 text-center">
            Don&apos;t have an account?{" "}
            <Link to="/signup" data-testid="goto-signup" className="text-propera font-semibold hover:underline">Create one</Link>
          </p>

          {process.env.REACT_APP_DEMO_MODE === "true" && (
            <div className="mt-8 pt-6 border-t border-neutral-100" data-testid="login-demo-accounts">
              <p className="text-[10px] uppercase tracking-widest text-neutral-400 mb-2">Demo Accounts</p>
              <div className="text-xs text-neutral-500 space-y-1">
                <p><b className="text-neutral-700">Investor:</b> investor@propera.com / Investor1234!</p>
                <p><b className="text-neutral-700">Developer:</b> developer@propera.com / Developer1234!</p>
              </div>
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-neutral-400 justify-center">
            <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className="hover:text-propera">Privacy Policy</a>
            <span className="text-neutral-300">·</span>
            <a href="/terms.html" target="_blank" rel="noopener noreferrer" className="hover:text-propera">Terms of Service</a>
            <span className="text-neutral-300">·</span>
            <a href="/cookies.html" target="_blank" rel="noopener noreferrer" className="hover:text-propera">Cookie Policy</a>
          </div>
        </div>
      </div>

      {/* Right visual */}
      <div className="hidden lg:flex flex-1 relative bg-gradient-to-br from-propera via-[#0B734A] to-[#06432B] overflow-hidden">
        <div className="absolute inset-0 grain" />
        <div className="relative z-10 flex flex-col justify-between p-16 text-white w-full">
          <p className="text-xs uppercase tracking-[0.3em] opacity-80">Propera</p>
          <div>
            <h2 className="serif text-6xl leading-[1.05] font-normal">
              Verified developments<br />
              across <span className="text-propera-gold italic">Africa.</span>
            </h2>
            <p className="mt-6 max-w-md opacity-80 text-base">
              From Lagos waterfronts to Accra sky towers, Propera connects you to vetted African developers with structured payment plans built for the diaspora.
            </p>
          </div>
          <p className="text-xs opacity-60">© 2026 Propera</p>
        </div>
      </div>
    </div>
  );
}
